// 統一的 fetch wrapper，處理 API 規格書 §1.3 envelope 與 §1.5 錯誤碼。

import { apiBaseUrl } from '../lib/env';
import type { ApiErrorCode, ApiResponse } from '../types/api';

export class ApiError extends Error {
  code: ApiErrorCode;
  status: number;
  details?: Record<string, unknown>;

  constructor(code: ApiErrorCode, message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

type QueryValue = string | number | boolean | undefined | null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  query?: { [key: string]: QueryValue };
  body?: unknown;
  /** 401 時不自動觸發全域 onUnauthorized callback（用於 /api/auth/me bootstrap） */
  silentOn401?: boolean;
}

let onUnauthorized: (() => void) | null = null;

/** 註冊 401 時的全域 callback（通常由 AuthContext 設成跳轉到 /login） */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

function buildUrl(path: string, query?: { [key: string]: QueryValue }): string {
  if (!query) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    sp.append(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    // 'include' 必要：prod 是 pages.dev → workers.dev 跨網域，需要送 session cookie
    credentials: 'include',
  };

  if (opts.body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(`${apiBaseUrl()}${buildUrl(path, opts.query)}`, init);

  let data: ApiResponse<T>;
  try {
    data = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError('INTERNAL_ERROR', `非預期回應 (HTTP ${res.status})`, res.status);
  }

  if (!data.success) {
    if (data.error.code === 'UNAUTHORIZED' && !opts.silentOn401) {
      onUnauthorized?.();
    }
    throw new ApiError(data.error.code, data.error.message, res.status, data.error.details);
  }
  return data.data;
}
